import { useMemo, useState } from 'react';
import { DataError, useList } from '@hana/data';
import { findCarrier } from '@hana/domain';
import { useT } from '@hana/i18n';
import { Page, PageHeader } from '@hana/shell';
import { Button, Chip, Input, StateView, Table, type Column, type ScreenState } from '@hana/ui';
import { IconSearch } from '@tabler/icons-react';
import { useP2PAccess } from './access';
import { dmHm } from './format';
import { VesselDrawer } from './VesselDrawer';
import { matchRow } from './setup';
import { remaining } from './vessel';
import type { Vessel } from './types';
import './VesselsScreen.i18n';
import s from './vessel.module.css';

export function VesselsScreen() {
  const t = useT();
  const access = useP2PAccess();
  const list = useList('vessel');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Vessel | null>(null);
  const all = (list.data?.items ?? []) as Vessel[];
  const shown = useMemo(() => all.filter((v) => matchRow([v.name, v.voyage, v.carrier, v.service, v.terminal, v.profile?.imo], q)), [all, q]);

  let state: ScreenState = 'ready';
  if (access.force) state = access.force;
  else if (list.isPending) state = 'loading';
  else if (list.error) state = list.error instanceof DataError && list.error.code === 'noperm' ? 'noperm' : 'error';
  else if (!all.length) state = 'empty';

  const cols: Column<Vessel>[] = [
    { key: 'name', header: t('vs.vessel'), cell: (v) => (<><b>{v.name}</b><div className={s.sub}>{v.voyage} · {v.carrier}</div></>) },
    { key: 'car', header: t('vs.carrier'), width: 90, minor: true, cell: (v) => <span title={findCarrier(v.carrier)?.name ?? v.carrier}>{v.carrier}</span> },
    { key: 'svc', header: 'Service', width: 90, minor: true, cell: (v) => v.service ?? '–' },
    { key: 'tml', header: 'Terminal', width: 170, minor: true, cell: (v) => v.terminal },
    { key: 'etd', header: 'ETD', width: 130, cell: (v) => dmHm(v.etd) },
    { key: 'cy', header: t('vs.cyLeft'), width: 100, minor: true, cell: (v) => { const r = remaining(v.cy); return <span className={r.cls === 'soon' ? s.warn : undefined}>{r.text || '–'}</span>; } },
    { key: 'chg', header: t('vs.changes'), width: 90, align: 'right', minor: true, cell: (v) => (v.changes ? <Chip tone="warn" label={t('vs.changesN', { n: v.changes })}>{v.changes}</Chip> : '–') },
    { key: 'imo', header: 'IMO', width: 110, minor: true, cell: (v) => (v.profile ? v.profile.imo : <span className={s.muted}>{t('vs.none')}</span>) },
  ];

  return (
    <>
      <Page kind="table">
      <PageHeader title={t('vs.title')} sub={t('vs.sub')} />
      <div className={s.page}>
        <StateView
          state={state}
          title={state === 'empty' ? t('vs.empty') : state === 'noperm' ? t('vs.noperm') : state === 'error' ? t('vs.err') : undefined}
          action={state === 'error' ? <Button onClick={() => void list.refetch()}>{t('vs.retry')}</Button> : undefined}
        >
          <div className={s.search}>
            <Input aria-label={t('vs.search')} placeholder={t('vs.searchPh')} icon={<IconSearch size={16} aria-hidden="true" />} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {shown.length === 0 ? (
            <StateView state="empty" title={t('vs.noMatch')} />
          ) : (
            <section className={s.listCard}>
              <Table label={t('vs.list')} columns={cols} rows={shown} rowKey={(v) => v.id} onRowClick={setOpen} dense />
            </section>
          )}
        </StateView>
      </div>
      </Page>
      <VesselDrawer vessel={open} onClose={() => setOpen(null)} />
    </>
  );
}
